/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/naming-convention */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Storage } from '@capacitor/storage';
import { environment } from 'src/environments/environment';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorService } from 'src/app/shared/services/http-error.service';
import { Router } from '@angular/router';

export interface AuthResponseData{
  superAdmin?: any;
  companyOwner: any;
  id: string;
  name: string;
  email: string;
  token: string;
  expiresIn: any;
  role: string;
  accountStatus: string;
  companyId: string;
}

//Models server side response object for login CRUD
export interface Response{
    validAgbVersion: boolean;
    failure: boolean;
    message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type':  'application/json',
      'Access-Control-Allow-Origin': `${environment.apiEndpoints.users}`,
      'Cache-Control': 'no-cache'
    })
  };

  user = new BehaviorSubject<AuthResponseData>(null);
  isloggedIn = new BehaviorSubject<boolean>(false);
  hasValidAgbVersion = new BehaviorSubject<boolean>(false);
  private authToken: string;
  private authData: AuthResponseData;
  private isAuthenticated: boolean;
  private readonly  userEndpoint = environment.apiEndpoints.users;
  private readonly  agbEndpoint = environment.apiEndpoints.agb;

  constructor(
    private http: HttpClient,
    private httpError: HttpErrorService,
    private router: Router) {}

   get token(){
    return this.authToken;
   }

   get isUserLogedIn(){
    return this.isloggedIn.asObservable().pipe(
      map(validLogin=>{
        if(!validLogin){
          return false;
        }
        return true;
      })
    );
   }

// returns logged in user object
   async getUserName(){
    if(!this.authData){
      await this.updateFromStrage();
       return this.user.asObservable().pipe(
        map(user=>{
          if(user){
            this.authToken = user.token;
            this.isloggedIn.next(true);
            return user.name;
          }
        })
       );
     }

     this.user.next(this.authData);
     return this.user.asObservable().pipe(map(user =>{
      if(user){
        this.isloggedIn.next(true);
        return user.name;
      }
     }));
   }

// persists auth data in localstorage
  async storeUserData(localData: any){
      await Storage.set( {key: 'authData', value:  JSON.stringify(localData)});
  }

  async removeUserData(){
      await Storage.set( {key: 'authData', value:  null});
  }

// retrieve auth data in localstorage
  async retrieveStoredData(){
    const storedData = await Storage.get({key: 'authData'});
   return JSON.parse(storedData.value);
 }

 // updates auth data from localstorage
async updateFromStrage(){
  return await this.retrieveStoredData().then(storedData =>{
    if(storedData){
      this.authData = storedData;
      this.authToken = storedData.token;
      this.user.next(this.authData);
      return storedData;
    }
    return null;
  });
 }

 loginData(loginData: any): Observable<AuthResponseData>{
      console.log(this.userEndpoint + '/login');
   return  this.http.post<AuthResponseData>(this.userEndpoint + '/login', loginData, this.httpOptions)
   .pipe(
     map(authData=> {
      this.authData = authData;
      //this.authData.expiresIn = new Date(( +authData.expiresIn * 1000 ) + new Date().getTime());
      this.storeUserData(this.authData);
      this.user.next(this.authData);
      this.authToken = this.authData.token;
      this.isloggedIn.next(true);
      return this.authData;
    }),
    catchError(error=> this.httpError.handleError(error))
   );
}

//login user with valid auth data
async autoLogin(){
  return this.updateFromStrage().
    then(storedData=>{
      if(!storedData){
        return null;
      }
      storedData = storedData as AuthResponseData;
      const expirationTime = new Date(storedData.expiresIn);
      const isValidLogin = new Date() < expirationTime;
      this.isloggedIn.next(isValidLogin);
      if(!isValidLogin){
        return isValidLogin;
      }
      return !!storedData;
    });

}


logout() {
    this.isloggedIn.next(false);
    this.user.next(null);
    this.removeUserData();
    Storage.remove({key: 'authData'});
    Storage.remove({key: 'companyOwner'});
    this.router.navigateByUrl('/login');
}

forgottenPsw(email: string): Observable<Response>{
  return this.http.get<Response>(this.userEndpoint + `/login/${email}`, this.httpOptions);
}

verifyLink(params: object): Observable<{valid: boolean; change: boolean; message?: string}>{
  return this.http.post<{valid: boolean; change: boolean; message?: string}>(this.userEndpoint + `/login/verify`, params, this.httpOptions);
}

updatePassword(token: string, password: string){
  return this.http.put<Response>(this.userEndpoint + '/login', {token, password}, this.httpOptions);
}

checkAgbVersion(){
    return this.http.get<Response>(this.agbEndpoint + '/has-valid-agb-version', this.httpOptions);
}
}
